import { Button, ButtonGroup, InfoBox, styled } from '@moleculeone/ui';
import { Analog } from '@retro-ui/api-contract';

import { AnalogsProps, PROMPT_BEFORE_LOSING_ANALOGS } from '../hooks/useAnalogs';

import { CompoundGrid } from './CompoundGrid';

const AnalogsSelectionContainer = styled('div', {
  height: '60vh',
  width: '100%',
  overflowY: 'scroll',
  padding: '$8',
  marginTop: '4.5rem',
});
const AnalogsSelectionActions = styled('div', {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  padding: '$5 $8',
  backgroundColor: 'white',
  borderRadius: '$medium $medium 0 0',
  display: 'flex',
  justifyContent: 'space-between',
  gap: '$4',
});
const AnalogsSelectionActionsGroup = styled('div', {
  display: 'flex',
  gap: '$4',
});

interface AnalogsSelectionProps extends AnalogsProps {
  analogs: Analog[];
  smiles: string;
  onAddSelectedAnalogs: () => void;
  onBackToEditor: () => void;
}
export function AnalogsSelection(props: AnalogsSelectionProps) {
  const {
    smiles,
    onAddSelectedAnalogs,
    onBackToEditor,
    analogs,
    setAnalogs,
    selectedAnalogs,
    setSelectedAnalogs,
    analogsPrompt,
    isFetchingAnalogExplanations,
    selectAllAnalogs,
    selectNoAnalogs,
    debugInfo,
    resetAnalogState,
  } = props;

  return (
    <AnalogsSelectionContainer>
      {debugInfo}

      {analogs.length === 0 ? (
        <InfoBox type="warning" icon="warning" title="No analogs found">
          Please try generating again or changing the prompt
          <br />
          <Button
            text="Change prompt"
            icon="settings"
            theme="purple"
            onClick={() => {
              setAnalogs(null);
            }}
            css={{ marginTop: '$6' }}
          />
        </InfoBox>
      ) : (
        <CompoundGrid
          prompt={analogsPrompt}
          originalCompoundSmiles={smiles}
          compounds={analogs}
          selectedCompounds={selectedAnalogs}
          setSelectedCompounds={setSelectedAnalogs}
          isFetchingAnalogExplanations={isFetchingAnalogExplanations}
        />
      )}
      <AnalogsSelectionActions>
        <AnalogsSelectionActionsGroup>
          <Button
            weight="minimal"
            text="Back to editor"
            icon="arrow_back"
            onClick={() => {
              const isConfirmed = analogs.length !== 0 ? window.confirm(PROMPT_BEFORE_LOSING_ANALOGS) : true;
              if (isConfirmed) {
                onBackToEditor();
              }
            }}
            size="small"
          />
          <Button
            weight="minimal"
            text="Change prompt"
            icon="settings"
            onClick={() => {
              const isConfirmed = analogs.length !== 0 ? window.confirm(PROMPT_BEFORE_LOSING_ANALOGS) : true;
              if (isConfirmed) {
                resetAnalogState();
              }
            }}
          />
        </AnalogsSelectionActionsGroup>
        <AnalogsSelectionActionsGroup>
          <ButtonGroup>
            <Button
              weight="outlined"
              onClick={selectAllAnalogs}
              text="Select all"
              icon="done_all"
              disabled={analogs.length === selectedAnalogs.length}
            />
            <Button
              weight="outlined"
              onClick={selectNoAnalogs}
              text="Select none"
              icon="deselect"
              disabled={selectedAnalogs.length === 0}
            />
          </ButtonGroup>
          <Button
            theme="purple"
            disabled={selectedAnalogs.length === 0}
            text={
              selectedAnalogs.length === 0 ? (
                'Select analogs to add to search'
              ) : (
                <>
                  Add <strong>{selectedAnalogs.length}</strong>{' '}
                  {selectedAnalogs.length === 1 ? 'compound' : 'compounds'} to search
                </>
              )
            }
            icon="settings"
            onClick={() => {
              resetAnalogState();
              onAddSelectedAnalogs();
            }}
          />
        </AnalogsSelectionActionsGroup>
      </AnalogsSelectionActions>
    </AnalogsSelectionContainer>
  );
}
